// oauth consent page - shown during authorization flow for external apps
import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { tokenService } from "../../services/tokenService";
import { getAccessToken, clearAuthTokens } from "../../utils/authTokens";
import "./oauthConsent.css";

interface ClientInfo {
    name: string;
    description?: string;
    logoUrl?: string;
}

interface ConsentPageData {
    client: ClientInfo;
    scopes: string[];
    params: {
        client_id: string;
        redirect_uri: string;
        scope: string;
        state?: string;
        nonce?: string;
        code_challenge?: string;
        code_challenge_method?: string;
    };
}

const SCOPE_DESCRIPTIONS: Record<string, { name: string; description: string }> = {
    openid: {
        name: "Basic Identity",
        description: "Confirm your identity",
    },
    profile: {
        name: "Profile",
        description: "Your username and display name",
    },
    email: {
        name: "Email",
        description: "Your email address and verification status",
    },
};

const OAuthConsentPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [consentData, setConsentData] = useState<ConsentPageData | null>(null);
    const [submitting, setSubmitting] = useState(false);

    const authBaseUrl = process.env.REACT_APP_AUTH_URL || "http://localhost:3045";

    // redirect to login with return url
    const redirectToLogin = useCallback(() => {
        const returnUrl = encodeURIComponent(window.location.href);
        navigate(`/login?return=${returnUrl}`);
    }, [navigate]);

    useEffect(() => {
        const fetchConsentData = async () => {
            try {
                // forward all query params to auth service
                const queryString = searchParams.toString();
                let accessToken = getAccessToken();

                if (!accessToken) {
                    // no token at all, redirect to login
                    redirectToLogin();
                    return;
                }

                let response: Response;
                try {
                    console.log("[OAuthConsent] Fetching:", `${authBaseUrl}/oauth/authorize?${queryString.substring(0, 50)}...`);
                    response = await fetch(`${authBaseUrl}/oauth/authorize?${queryString}`, {
                        headers: {
                            Authorization: `Bearer ${accessToken}`,
                            Accept: 'application/json', // tell auth service we want JSON, not redirects
                        },
                    });
                    console.log("[OAuthConsent] Response status:", response.status);
                } catch (fetchError: any) {
                    // network error - could be CORS, server not running, or network issue
                    console.error("[OAuthConsent] Fetch error:", fetchError);
                    console.error("[OAuthConsent] Error name:", fetchError?.name);
                    console.error("[OAuthConsent] Error message:", fetchError?.message);

                    // check if it might be a CORS error
                    const errorMsg = fetchError?.message || "";
                    if (errorMsg.includes("CORS") || errorMsg.includes("cross-origin")) {
                        throw new Error(`CORS error: The auth service at ${authBaseUrl} is not allowing requests from this origin. Check CORS_ORIGINS in auth service .env`);
                    }
                    throw new Error(`Cannot reach auth service at ${authBaseUrl}. Error: ${fetchError?.message || 'Network error'}. Check browser console for details.`);
                }

                // if unauthorized (401), try to refresh the token
                if (response.status === 401) {
                    console.log("[OAuthConsent] Got 401, attempting refresh...");
                    const tokens = await tokenService.refresh();
                    if (tokens) {
                        console.log("[OAuthConsent] Refresh succeeded, retrying request...");
                        // retry with new token
                        try {
                            response = await fetch(`${authBaseUrl}/oauth/authorize?${queryString}`, {
                                headers: {
                                    Authorization: `Bearer ${tokens.accessToken}`,
                                    Accept: 'application/json',
                                },
                            });
                        } catch (fetchError: any) {
                            throw new Error(`Cannot reach auth service at ${authBaseUrl}. Is it running?`);
                        }
                    } else {
                        // refresh failed, redirect to login
                        console.log("[OAuthConsent] Refresh failed, redirecting to login...");
                        // use window.location for more reliable redirect
                        const returnUrl = encodeURIComponent(window.location.href);
                        window.location.href = `/login?return=${returnUrl}`;
                        return;
                    }
                }

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    // check for OAuth errors
                    if (errorData.error) {
                        throw new Error(`${errorData.error}: ${errorData.error_description || 'Unknown error'}`);
                    }
                    throw new Error(errorData.message || `Auth service returned ${response.status}`);
                }

                const data = await response.json();

                // check if auth service wants us to redirect (trusted client auto-approve)
                if (data.redirect && data.redirectUrl) {
                    console.log("[OAuthConsent] Auto-approved, redirecting to:", data.redirectUrl);
                    window.location.href = data.redirectUrl;
                    return;
                }

                setConsentData(data.data || data);
            } catch (err: any) {
                console.error("OAuth consent error:", err);
                setError(err.message || "failed to load consent page");
            } finally {
                setLoading(false);
            }
        };

        fetchConsentData();
    }, [searchParams, authBaseUrl, redirectToLogin]);

    const handleDecision = async (decision: "approve" | "deny") => {
        if (!consentData) return;

        setSubmitting(true);
        try {
            let accessToken = getAccessToken();

            const makeRequest = async (token: string) => {
                return fetch(`${authBaseUrl}/oauth/authorize`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        ...consentData.params,
                        decision,
                    }),
                });
            };

            let response = await makeRequest(accessToken || "");

            // if unauthorized, try to refresh token
            if (response.status === 401) {
                const tokens = await tokenService.refresh();
                if (tokens) {
                    response = await makeRequest(tokens.accessToken);
                } else {
                    redirectToLogin();
                    return;
                }
            }

            if (response.redirected) {
                window.location.href = response.url;
            } else {
                // follow redirect manually
                const location = response.headers.get("Location");
                if (location) {
                    window.location.href = location;
                } else {
                    // try to parse redirect from response
                    const html = await response.text();
                    const match = html.match(/redirect_uri['"]\s*:\s*['"]([^'"]+)/);
                    if (match) {
                        window.location.href = match[1];
                    }
                }
            }
        } catch (err: any) {
            setError(err.message || "failed to submit decision");
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="oauth-consent-page">
                <div className="consent-loading">
                    <div className="spinner"></div>
                    <p>Loading...</p>
                </div>
            </div>
        );
    }

    if (error) {
        // distinguish between auth errors (need to login) and OAuth config errors
        const isAuthError = (error.includes("expired") || error.includes("401") || error.includes("token"))
            && !error.includes("redirect_uri") && !error.includes("client_id");
        const isOAuthConfigError = error.includes("redirect_uri") || error.includes("client_id") || error.includes("invalid_request");

        return (
            <div className="oauth-consent-page">
                <div className="consent-error">
                    <h2>Authorization Error</h2>
                    <p>{error}</p>

                    {isOAuthConfigError && (
                        <div className="error-help">
                            <p><strong>This is an OAuth configuration error.</strong></p>
                            <p>The application trying to authenticate has an invalid configuration.
                                Please contact the app developer or check that the redirect URI is registered.</p>
                        </div>
                    )}

                    <div className="error-actions">
                        {isAuthError && (
                            <button
                                onClick={() => {
                                    // clear tokens before redirecting to ensure fresh login
                                    clearAuthTokens();
                                    const returnUrl = encodeURIComponent(window.location.href);
                                    window.location.href = `/login?return=${returnUrl}`;
                                }}
                                className="primary-btn"
                            >
                                Login Again
                            </button>
                        )}
                        <button onClick={() => window.history.back()}>Go Back</button>
                    </div>
                </div>
            </div>
        );
    }

    if (!consentData) {
        return null;
    }

    const { client, scopes } = consentData;

    return (
        <div className="oauth-consent-page">
            <div className="consent-card">
                <div className="consent-header">
                    {client.logoUrl && (
                        <img src={client.logoUrl} alt={client.name} className="client-logo" />
                    )}
                    <h1>
                        <span className="client-name">{client.name}</span> wants to access
                        your account
                    </h1>
                    {client.description && (
                        <p className="client-description">{client.description}</p>
                    )}
                </div>

                <div className="consent-scopes">
                    <h3>This will allow {client.name} to:</h3>
                    <ul>
                        {scopes.map((scope) => {
                            const scopeInfo = SCOPE_DESCRIPTIONS[scope];
                            if (!scopeInfo) return null;
                            return (
                                <li key={scope}>
                                    <span className="scope-name">{scopeInfo.name}</span>
                                    <span className="scope-desc">{scopeInfo.description}</span>
                                </li>
                            );
                        })}
                    </ul>
                </div>

                <div className="consent-warning">
                    <p>
                        Make sure you trust this application. By authorizing, you agree to
                        share your information with {client.name}.
                    </p>
                </div>

                <div className="consent-actions">
                    <button
                        onClick={() => handleDecision("deny")}
                        disabled={submitting}
                        className="deny-btn"
                    >
                        Deny
                    </button>
                    <button
                        onClick={() => handleDecision("approve")}
                        disabled={submitting}
                        className="approve-btn"
                    >
                        {submitting ? "Authorizing..." : "Authorize"}
                    </button>
                </div>

                <div className="consent-footer">
                    <p>
                        Authorizing will redirect you to <code>{consentData.params.redirect_uri}</code>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default OAuthConsentPage;
